// Section registry for the export system.
// Column def: { header, get: (row) => raw value, type: 'text'|'number'|'percent'|'duration'|'date' }
// Section def: { key, label, description, type: 'metrics'|'table', default, needs,
//   available: (data) => bool, getMetrics?: (data) => [{label, value, type}],
//   columns?: [...], resolveColumns?: (rows) => [...], getRows?: (data, config) => [...] }
import { formatPeriodLabel } from './exportUtils';

const toNum = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const capitalize = (str) => (str ? String(str).charAt(0).toUpperCase() + String(str).slice(1) : '');

const pathnameOf = (url) => {
  try {
    return new URL(url).pathname;
  } catch (e) {
    return url || '';
  }
};

const nonNullish = (metrics) => metrics.filter((m) => m.value !== null && m.value !== undefined);

const alwaysAvailable = () => true;

const impressionsCol = { header: 'Impressions', get: (r) => r.impressions, type: 'number' };
const clicksCol = { header: 'Clicks', get: (r) => r.clicks, type: 'number' };
const ctrCol = { header: 'CTR %', get: (r) => toNum(r.ctr), type: 'percent' };

// Traffic buckets are hourly when any two consecutive rows are less than 23h apart
// (useTrafficData returns hourly buckets for ranges spanning <= 1 day)
const isHourlyRows = (rows) => rows.some((r, i) =>
  i > 0 && Math.abs(new Date(r.date) - new Date(rows[i - 1].date)) < 23 * 60 * 60 * 1000
);

const TRAFFIC_METRIC_COLUMNS = [
  { header: 'Visitors', get: (r) => r.visitors, type: 'number' },
  { header: 'Sessions', get: (r) => r.visits, type: 'number' },
  { header: 'Pageviews', get: (r) => r.pageviews, type: 'number' },
];

// Sections shared between analytics and dashboard (same data shapes)
const trafficSection = {
  key: 'traffic',
  label: 'Traffic Over Time',
  description: 'Visitors, sessions and pageviews per day',
  type: 'table',
  default: true,
  needs: null,
  available: (data) => Array.isArray(data.trafficData),
  resolveColumns: (rows) => [
    { header: 'Date', get: (r) => r.date, type: isHourlyRows(rows) ? 'datetime' : 'date' },
    ...TRAFFIC_METRIC_COLUMNS,
  ],
  getRows: (data) => data.trafficData || [],
};

const sourcesSection = {
  key: 'sources',
  label: 'Traffic Sources',
  description: 'Visits broken down by channel and source',
  type: 'table',
  default: true,
  needs: null,
  available: (data) => Array.isArray(data.sourcesData),
  columns: [
    { header: 'Channel', get: (r) => r.channel, type: 'text' },
    { header: 'Source', get: (r) => r.source, type: 'text' },
    { header: 'Visitors', get: (r) => r.visitors, type: 'number' },
    { header: 'Sessions', get: (r) => r.visits, type: 'number' },
  ],
  getRows: (data) => (data.sourcesData || []).flatMap((category) => {
    const children = category.type === 'search' ? category.sources : category.details;
    const rows = (children || []).map((child) => ({
      channel: capitalize(category.type),
      source: child.name,
      visitors: child.visitors,
      visits: child.visits,
    }));
    return rows.length > 0
      ? rows
      : [{ channel: capitalize(category.type), source: category.name, visitors: category.visitors, visits: category.visits }];
  }),
};

const devicesSection = {
  key: 'devices',
  label: 'Devices',
  description: 'Visitor share by device type',
  type: 'table',
  default: true,
  needs: null,
  available: (data) => Array.isArray(data.devicesData),
  columns: [
    { header: 'Device', get: (r) => capitalize(r.device), type: 'text' },
    { header: 'Visitors', get: (r) => r.visitors, type: 'number' },
    { header: 'Share %', get: (r) => toNum(r.percentage), type: 'percent' },
  ],
  getRows: (data) => data.devicesData || [],
};

export const EXPORT_SECTIONS = {
  reports: [
    {
      key: 'summary',
      label: 'Executive Summary',
      description: 'Key performance metrics at a glance',
      type: 'metrics',
      default: true,
      needs: 'audience',
      available: alwaysAvailable,
      getMetrics: (data) => {
        const o = data.overview || {};
        const reach = data.audience?.reach || {};
        return nonNullish([
          { label: 'Impressions', value: o.totalImpressions, type: 'number' },
          { label: 'Clicks', value: o.totalClicks, type: 'number' },
          { label: 'CTR %', value: o.ctr, type: 'percent' },
          { label: 'Unique Reach', value: reach.uniqueVisitors, type: 'number' },
          { label: 'Avg Frequency', value: reach.frequency, type: 'number' },
          { label: 'Avg Time on Page', value: o.avgTimeOnPage, type: 'duration' },
          { label: 'Avg Session', value: o.avgSessionDuration, type: 'duration' },
          { label: 'Page Views', value: o.pageViews, type: 'number' },
        ]);
      },
    },
    {
      key: 'campaigns',
      label: 'Campaign Performance',
      description: 'Totals for each campaign',
      type: 'table',
      default: true,
      needs: null,
      available: alwaysAvailable,
      columns: [
        { header: 'Campaign', get: (r) => r.name, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.campaignData || [],
    },
    {
      key: 'campaignPlacements',
      label: 'Campaign Placements (Campaign × Zone)',
      description: 'Each campaign broken down by the zones it ran in',
      type: 'table',
      default: false,
      needs: null,
      available: alwaysAvailable,
      columns: [
        { header: 'Campaign', get: (r) => r.campaign, type: 'text' },
        { header: 'Zone', get: (r) => r.name, type: 'text' },
        { header: 'Site', get: (r) => r.site, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => (data.campaignData || []).flatMap((c) =>
        (c.zones || []).map((z) => ({ campaign: c.name, ...z }))
      ),
    },
    {
      key: 'zones',
      label: 'Zone Performance',
      description: 'Totals for each ad zone',
      type: 'table',
      default: true,
      needs: null,
      available: alwaysAvailable,
      columns: [
        { header: 'Zone', get: (r) => r.name, type: 'text' },
        { header: 'Site', get: (r) => r.site, type: 'text' },
        { header: 'Size', get: (r) => r.size, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.zoneData || [],
    },
    {
      key: 'ads',
      label: 'Ad Performance',
      description: 'Totals for each individual ad',
      type: 'table',
      default: true,
      needs: null,
      available: alwaysAvailable,
      columns: [
        { header: 'Ad', get: (r) => r.name, type: 'text' },
        { header: 'Type', get: (r) => r.type, type: 'text' },
        { header: 'Size', get: (r) => r.size, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.adData || [],
    },
    {
      key: 'adPlacements',
      label: 'Ad Placement Detail',
      description: 'Each ad broken down by campaign and zone',
      type: 'table',
      default: false,
      needs: null,
      available: alwaysAvailable,
      columns: [
        { header: 'Ad', get: (r) => r.ad, type: 'text' },
        { header: 'Campaign', get: (r) => r.campaignName, type: 'text' },
        { header: 'Zone', get: (r) => r.zoneName, type: 'text' },
        { header: 'Site', get: (r) => r.site, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => (data.adData || []).flatMap((a) =>
        (a.breakdowns || []).map((b) => ({ ad: a.name, ...b }))
      ),
    },
    {
      key: 'trend',
      label: 'Performance Over Time',
      description: 'Impressions and clicks per period',
      type: 'table',
      default: true,
      needs: 'trend',
      available: alwaysAvailable,
      columns: [
        { header: 'Period', get: (r) => r.period, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data, config) => (data.trend || []).map((r) => ({
        period: formatPeriodLabel(r.date, config.granularity),
        impressions: r.impressions,
        clicks: r.clicks,
        ctr: r.ctr,
      })),
    },
    {
      key: 'adPeriods',
      label: 'Ads by Period',
      description: 'Per-ad performance for each period',
      type: 'table',
      default: false,
      needs: 'adPeriods',
      available: alwaysAvailable,
      columns: [
        { header: 'Ad', get: (r) => r.ad, type: 'text' },
        { header: 'Period', get: (r) => r.period, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data, config) => (data.adPeriods || []).flatMap((ad) =>
        (ad.daily || []).map((d) => ({
          ad: ad.name,
          period: formatPeriodLabel(d.date, config.granularity),
          impressions: d.impressions,
          clicks: d.clicks,
          ctr: d.ctr,
        }))
      ),
    },
    {
      key: 'campaignPeriods',
      label: 'Campaigns by Period',
      description: 'Per-campaign performance for each period',
      type: 'table',
      default: false,
      needs: 'campaignPeriods',
      available: alwaysAvailable,
      columns: [
        { header: 'Campaign', get: (r) => r.campaign, type: 'text' },
        { header: 'Period', get: (r) => r.period, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data, config) => (data.campaignPeriods || []).flatMap((c) =>
        (c.daily || []).map((d) => ({
          campaign: c.name,
          period: formatPeriodLabel(d.date, config.granularity),
          impressions: d.impressions,
          clicks: d.clicks,
          ctr: d.ctr,
        }))
      ),
    },
    {
      key: 'zonePeriods',
      label: 'Zones by Period',
      description: 'Per-zone performance for each period',
      type: 'table',
      default: false,
      needs: 'zonePeriods',
      available: alwaysAvailable,
      columns: [
        { header: 'Zone', get: (r) => r.zone, type: 'text' },
        { header: 'Period', get: (r) => r.period, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data, config) => (data.zonePeriods || []).flatMap((z) =>
        (z.daily || []).map((d) => ({
          zone: z.name,
          period: formatPeriodLabel(d.date, config.granularity),
          impressions: d.impressions,
          clicks: d.clicks,
          ctr: d.ctr,
        }))
      ),
    },
    {
      key: 'geo',
      label: 'Audience Geography',
      description: 'Impressions and clicks by country',
      type: 'table',
      default: true,
      needs: 'audience',
      available: alwaysAvailable,
      columns: [
        { header: 'Country', get: (r) => r.country, type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.audience?.geo || [],
    },
    {
      key: 'devices',
      label: 'Audience Devices',
      description: 'Impressions and clicks by device type',
      type: 'table',
      default: true,
      needs: 'audience',
      available: alwaysAvailable,
      columns: [
        { header: 'Device', get: (r) => capitalize(r.device), type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.audience?.devices || [],
    },
    {
      key: 'adPages',
      label: 'Top Pages for Ads',
      description: 'Pages where ads were seen most',
      type: 'table',
      default: false,
      needs: 'audience',
      available: alwaysAvailable,
      columns: [
        { header: 'Page', get: (r) => pathnameOf(r.page), type: 'text' },
        impressionsCol,
        clicksCol,
        ctrCol,
      ],
      getRows: (data) => data.audience?.topPages || [],
    },
  ],
  analytics: [
    {
      key: 'summary',
      label: 'Executive Summary',
      description: 'Key traffic metrics at a glance',
      type: 'metrics',
      default: true,
      needs: null,
      available: alwaysAvailable,
      getMetrics: (data) => {
        const o = data.overview || {};
        return nonNullish([
          { label: 'Visitors', value: o.uniqueVisitors, type: 'number' },
          { label: 'Sessions', value: o.totalVisits, type: 'number' },
          { label: 'Pageviews', value: o.totalPageviews, type: 'number' },
          { label: 'Bounce Rate %', value: o.bounceRate, type: 'percent' },
          { label: 'Engagement Rate %', value: o.engagementRate, type: 'percent' },
          { label: 'Avg Session', value: o.avgSessionDuration, type: 'duration' },
          { label: 'Avg Time on Page', value: o.avgTimeOnPage, type: 'duration' },
          { label: 'New Visitors %', value: o.newVisitorPercentage, type: 'percent' },
        ]);
      },
    },
    trafficSection,
    {
      key: 'pages',
      label: 'Top Pages',
      description: 'Most viewed pages with engagement metrics',
      type: 'table',
      default: true,
      needs: null,
      available: (data) => Array.isArray(data.pagesData),
      columns: [
        { header: 'Page', get: (r) => r.path, type: 'text' },
        { header: 'Views', get: (r) => r.views, type: 'number' },
        { header: 'Unique', get: (r) => r.uniqueViews, type: 'number' },
        { header: 'Avg Time', get: (r) => r.avgTime, type: 'duration' },
        { header: 'Entrances', get: (r) => r.entrances, type: 'number' },
        { header: 'Bounce %', get: (r) => toNum(r.bounceRate), type: 'percent' },
        { header: 'Exit %', get: (r) => toNum(r.exitRate), type: 'percent' },
      ],
      getRows: (data) => data.pagesData || [],
    },
    sourcesSection,
    {
      key: 'geo',
      label: 'Geography',
      description: 'Visitors and pageviews by country',
      type: 'table',
      default: true,
      needs: null,
      available: (data) => Array.isArray(data.geoData),
      columns: [
        { header: 'Country', get: (r) => r.country || r.country_name, type: 'text' },
        { header: 'Visitors', get: (r) => r.visitors, type: 'number' },
        { header: 'Pageviews', get: (r) => r.pageviews, type: 'number' },
        { header: 'Share %', get: (r) => toNum(r.percentage), type: 'percent' },
      ],
      getRows: (data) => data.geoData || [],
    },
    devicesSection,
    {
      key: 'landing',
      label: 'Landing Pages',
      description: 'Entry pages with bounce and duration metrics',
      type: 'table',
      default: false,
      needs: null,
      available: (data) => Array.isArray(data.landingPages),
      columns: [
        { header: 'URL', get: (r) => r.url, type: 'text' },
        { header: 'Sessions', get: (r) => r.sessions, type: 'number' },
        { header: 'Visitors', get: (r) => r.visitors, type: 'number' },
        { header: 'Bounce %', get: (r) => toNum(r.bounceRate), type: 'percent' },
        { header: 'Avg Duration', get: (r) => r.avgDuration, type: 'duration' },
      ],
      getRows: (data) => data.landingPages || [],
    },
    {
      key: 'exit',
      label: 'Exit Pages',
      description: 'Pages visitors left the site from',
      type: 'table',
      default: false,
      needs: null,
      available: (data) => Array.isArray(data.exitPages),
      columns: [
        { header: 'URL', get: (r) => r.url, type: 'text' },
        { header: 'Exits', get: (r) => r.exits, type: 'number' },
        { header: 'Pageviews', get: (r) => r.pageviews, type: 'number' },
        { header: 'Exit Rate %', get: (r) => toNum(r.exitRate), type: 'percent' },
      ],
      getRows: (data) => data.exitPages || [],
    },
  ],
  dashboard: [
    {
      key: 'summary',
      label: 'Executive Summary',
      description: 'Key metrics at a glance',
      type: 'metrics',
      default: true,
      needs: null,
      available: alwaysAvailable,
      getMetrics: (data) => {
        const o = data.overview || {};
        return nonNullish([
          { label: 'Visitors', value: o.uniqueVisitors, type: 'number' },
          { label: 'Pageviews', value: o.totalPageviews, type: 'number' },
          { label: 'Ad Impressions', value: o.adImpressions, type: 'number' },
          { label: 'Ad Clicks', value: o.adClicks, type: 'number' },
          { label: 'CTR %', value: o.ctr, type: 'percent' },
          { label: 'Avg Time on Page', value: o.avgTimeOnPage, type: 'duration' },
        ]);
      },
    },
    trafficSection,
    sourcesSection,
    devicesSection,
  ],
};

// Sections whose rows are synthesized as a single "Full Run" bucket when granularity is 'all'
const FULL_RUN_SECTIONS = ['trend', 'adPeriods', 'campaignPeriods', 'zonePeriods'];

const synthesizeFullRunRows = (key, data) => {
  if (key === 'trend') {
    const o = data.overview || {};
    return [{
      period: 'Full Run',
      impressions: o.totalImpressions || 0,
      clicks: o.totalClicks || 0,
      ctr: o.ctr || 0,
    }];
  }
  if (key === 'adPeriods') {
    return (data.adData || []).map((ad) => ({
      ad: ad.name,
      period: 'Full Run',
      impressions: ad.impressions || 0,
      clicks: ad.clicks || 0,
      ctr: ad.ctr,
    }));
  }
  if (key === 'zonePeriods') {
    return (data.zoneData || []).map((z) => ({
      zone: z.name,
      period: 'Full Run',
      impressions: z.impressions || 0,
      clicks: z.clicks || 0,
      ctr: z.ctr,
    }));
  }
  return (data.campaignData || []).map((c) => ({
    campaign: c.name,
    period: 'Full Run',
    impressions: c.impressions || 0,
    clicks: c.clicks || 0,
    ctr: c.ctr,
  }));
};

// Filter the reports totals tables down to a drill-down entity
// ({ type: 'campaign'|'zone'|'ad', id, name }). Linkage comes from the detailed
// endpoints: campaignData[].zones[].id and adData[].breakdowns[].{campaignId,zoneId}.
// Period arrays (adPeriods/campaignPeriods/zonePeriods) are trimmed to the scoped ids.
export const scopeReportsData = (data, entity) => {
  if (!entity) return data;
  const campaignData = data.campaignData || [];
  const zoneData = data.zoneData || [];
  const adData = data.adData || [];
  let campaigns = campaignData;
  let zones = zoneData;
  let ads = adData;

  if (entity.type === 'campaign') {
    campaigns = campaignData.filter((c) => c.id === entity.id);
    const zoneIds = new Set(campaigns.flatMap((c) => (c.zones || []).map((z) => z.id)));
    zones = zoneData.filter((z) => zoneIds.has(z.id));
    ads = adData.filter((a) => (a.breakdowns || []).some((b) => b.campaignId === entity.id));
  } else if (entity.type === 'zone') {
    zones = zoneData.filter((z) => z.id === entity.id);
    campaigns = campaignData.filter((c) => (c.zones || []).some((z) => z.id === entity.id));
    ads = adData.filter((a) => (a.breakdowns || []).some((b) => b.zoneId === entity.id));
  } else if (entity.type === 'ad') {
    ads = adData.filter((a) => a.id === entity.id);
    const breakdowns = ads.flatMap((a) => a.breakdowns || []);
    const campaignIds = new Set(breakdowns.map((b) => b.campaignId));
    const zoneIds = new Set(breakdowns.map((b) => b.zoneId));
    campaigns = campaignData.filter((c) => campaignIds.has(c.id));
    zones = zoneData.filter((z) => zoneIds.has(z.id));
  }

  const scoped = { ...data, campaignData: campaigns, zoneData: zones, adData: ads };
  const keepIds = (list) => new Set(list.map((r) => r.id));
  if (Array.isArray(data.adPeriods)) {
    const ids = keepIds(ads);
    scoped.adPeriods = data.adPeriods.filter((a) => ids.has(a.id));
  }
  if (Array.isArray(data.campaignPeriods)) {
    const ids = keepIds(campaigns);
    scoped.campaignPeriods = data.campaignPeriods.filter((c) => ids.has(c.id));
  }
  if (Array.isArray(data.zonePeriods)) {
    const ids = keepIds(zones);
    scoped.zonePeriods = data.zonePeriods.filter((z) => ids.has(z.id));
  }
  return scoped;
};

export const resolveSections = (pageType, config, data) => {
  const defs = EXPORT_SECTIONS[pageType] || [];
  const chosen = config.sections?.[pageType] || {};
  const resolved = [];

  defs.forEach((def) => {
    const isSelected = chosen[def.key] === undefined ? def.default : !!chosen[def.key];
    if (!isSelected || !def.available(data)) return;

    if (def.type === 'metrics') {
      const metrics = def.getMetrics(data);
      if (metrics.length === 0) return;
      resolved.push({ key: def.key, label: def.label, type: 'metrics', metrics });
      return;
    }

    const rows = pageType === 'reports' && config.granularity === 'all' && FULL_RUN_SECTIONS.includes(def.key)
      ? synthesizeFullRunRows(def.key, data)
      : def.getRows(data, config);
    if (!rows || rows.length === 0) return;
    const columns = def.resolveColumns ? def.resolveColumns(rows) : def.columns;
    resolved.push({ key: def.key, label: def.label, type: 'table', columns, rows });
  });

  return resolved;
};
